import { useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { getPublicProgram, type PublicProgram } from "@/services/training"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { GraduationCap, AlertTriangle, Clock, Tag, Lock } from "lucide-react"
import { TenantClosedNotice } from "@/components/TenantClosedNotice"
import { isTenantClosedError } from "@/lib/tenant-closed"

function formatMoney(value: number): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value)
}

/** Tarif public affiché selon le mode de tarification (Qualiopi Ind. 1, ticket #35). */
function renderTariff(program: PublicProgram): string {
  if (program.pricing_mode === "hourly" && program.hourly_rate_ht !== null) {
    return `${formatMoney(program.hourly_rate_ht)} HT / heure`
  }
  if (program.pricing_mode === "daily" && program.daily_rate_ht !== null) {
    return `${formatMoney(program.daily_rate_ht)} HT / jour`
  }
  const tiers = [
    program.price_inter !== null ? `Inter-entreprises : ${formatMoney(program.price_inter)} HT` : null,
    program.price_intra !== null ? `Intra-entreprise : ${formatMoney(program.price_intra)} HT` : null,
    program.price_individual !== null ? `Individuel : ${formatMoney(program.price_individual)} HT` : null,
  ].filter((tier): tier is string => tier !== null)
  return tiers.length > 0 ? tiers.join(" · ") : "Sur devis"
}

/**
 * Modalités de financement déclarées (indicateur 1), avec la réserve.
 * Rien quand aucune n'est déclarée.
 * @example <FinancingModalities labels={["OPCO"]} />
 */
export function FinancingModalities({ labels }: { labels: string[] }) {
  if (labels.length === 0) return null
  return (
    <Card data-test="program-public-financing">
      <CardHeader><CardTitle className="text-base">Modalités de financement</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-muted-foreground">Selon l'éligibilité du bénéficiaire et l'accord du financeur.</p>
        <ul className="list-disc space-y-1 ps-5">
          {labels.map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

/**
 * Type de reconnaissance, en libellé développé (indicateur 1).
 * Rien quand aucun type n'est choisi.
 * @example <RecognitionLabel label={O.some("Formation non certifiante")} />
 */
export function RecognitionLabel({ label }: { label: O.Option<string> }) {
  return pipe(
    label,
    O.match(
      () => null,
      (text) => (
        <p className="text-sm" data-test="program-public-recognition">
          <span className="font-medium">Reconnaissance de la formation : </span>
          {text}
        </p>
      ),
    ),
  )
}

export default function PublicProgramPage() {
  const { token } = useParams<{ token: string }>()
  const [passwordInput, setPasswordInput] = useState("")
  const [password, setPassword] = useState<string | undefined>(undefined)

  const { data: program, isLoading, isError, error } = useQuery({
    queryKey: ["public-program", token, password],
    queryFn: () => getPublicProgram(token!, password),
    enabled: !!token,
    retry: false,
  })

  if (!token) {
    return <PublicShell><ErrorState message="Lien invalide : jeton manquant." /></PublicShell>
  }

  if (isLoading) {
    return (
      <PublicShell>
        <div className="p-8 text-center text-muted-foreground">Chargement du programme…</div>
      </PublicShell>
    )
  }

  if (isTenantClosedError(error)) {
    return (
      <PublicShell>
        <TenantClosedNotice />
      </PublicShell>
    )
  }

  if (isError || !program) {
    const message = (error as Error)?.message ?? ""
    // Lien protégé par mot de passe : proposer la saisie.
    if (message.toLowerCase().includes("unauthorized") || message.includes("401")) {
      return (
        <PublicShell>
          <div className="mx-auto mt-12 max-w-md p-8">
            <div className="text-center">
              <Lock className="mx-auto h-10 w-10 text-primary" />
              <h2 className="mt-3 text-lg font-semibold">Programme protégé</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Ce programme nécessite un mot de passe pour être consulté.
              </p>
            </div>
            <form
              className="mt-6 space-y-3"
              onSubmit={(e) => {
                e.preventDefault()
                setPassword(passwordInput)
              }}
            >
              <div>
                <Label htmlFor="program-password">Mot de passe</Label>
                <Input
                  id="program-password"
                  type="password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  data-test="program-password-input"
                />
              </div>
              <Button type="submit" className="w-full" data-test="program-password-submit">
                Accéder au programme
              </Button>
            </form>
          </div>
        </PublicShell>
      )
    }
    return (
      <PublicShell>
        <ErrorState message={message || "Ce lien est invalide, révoqué ou expiré."} />
      </PublicShell>
    )
  }

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-8">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" data-test="program-public-type">{program.program_type}</Badge>
            {program.rncp_code && <Badge variant="secondary">RNCP {program.rncp_code}</Badge>}
          </div>
          <h1 className="text-2xl font-semibold" data-test="program-public-title">{program.title}</h1>
          <RecognitionLabel label={O.fromNullable(program.certification_type_label)} />
          {program.short_description && (
            <p className="text-muted-foreground">{program.short_description}</p>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tarif et durée</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-primary" />
              <span data-test="program-public-tariff" className="font-medium">{renderTariff(program)}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span data-test="program-public-duration">
                {program.duration_hours} heures
                {program.duration_days !== null ? ` (${program.duration_days} jours)` : ""}
              </span>
            </div>
            {program.access_delay_days !== null && (
              <p className="text-muted-foreground">
                Délai d'accès : {program.access_delay_days} jours
                {program.access_modalities ? ` — ${program.access_modalities}` : ""}
              </p>
            )}
          </CardContent>
        </Card>

        {program.full_description && (
          <Section title="Présentation">{program.full_description}</Section>
        )}

        {program.objectives.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Objectifs pédagogiques</CardTitle></CardHeader>
            <CardContent>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {program.objectives.map((objective) => (
                  <li key={objective.id}>{objective.description}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {program.prerequisites_list.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Prérequis</CardTitle></CardHeader>
            <CardContent>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {program.prerequisites_list.map((prerequisite) => (
                  <li key={prerequisite.id}>{prerequisite.description}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {program.target_audience && <Section title="Public visé">{program.target_audience}</Section>}
        {program.teaching_methods && <Section title="Modalités pédagogiques">{program.teaching_methods}</Section>}
        {program.assessment_methods && <Section title="Modalités d'évaluation">{program.assessment_methods}</Section>}
        <FinancingModalities labels={program.financing_modalities} />
        {program.accessibility_info && (
          <Section title="Accessibilité aux personnes en situation de handicap">
            {program.accessibility_info}
          </Section>
        )}

        {program.modules.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Programme détaillé</CardTitle></CardHeader>
            <CardContent>
              <ol className="space-y-2 text-sm">
                {program.modules.map((module) => (
                  <li key={module.code} className="flex items-baseline justify-between gap-4">
                    <span className="font-medium">{module.title}</span>
                    <span className="shrink-0 text-muted-foreground">{module.duration_hours} h</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        )}
      </div>
    </PublicShell>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="whitespace-pre-line text-sm text-muted-foreground">{children}</CardContent>
    </Card>
  )
}

function PublicShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-2 p-4 text-base font-medium">
          <GraduationCap className="h-5 w-5 text-primary" />
          Qualneo
        </div>
      </header>
      <main>{children}</main>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="mx-auto mt-12 max-w-md p-8 text-center">
      <AlertTriangle className="mx-auto h-10 w-10 text-orange-500" />
      <h2 className="mt-3 text-lg font-semibold">Lien indisponible</h2>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
    </div>
  )
}
